import { BudgetExpenseOrIncome } from 'business/constants';
import { FilterOperations } from 'business/definitions';
import { snakify } from 'helpers/syntaxHelper';
import { transformUrlParams } from 'helpers/urlHelpers';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryToSearch } from 'routes/Explore/components/Sentence/helpers';
import { RQAPICallWithData } from 'store/APIClient';
import { ALL_BUDGETS_VALUE } from 'routes/BudgetModule/components/BudgetOverview/constants';
import {
    BUDGET_BALANCE_TOTAL_FIELD,
    BUDGET_BALANCE_TOTAL_FIELD_WITH_REPORT,
    BUDGET_ID_FIELD,
    BUDGET_NAME_FIELD,
    BUDGET_YEAR_FIELD,
    CHAPTER_FIELD,
    CHAPTER_LABEL_FIELD,
    EXPENSE_INCOME_FIELD,
    NATURE_FIELD,
    PRIMITIVE_BUDGET_AMOUNT,
    PRIMITIVE_BUDGET_AMOUNT_WITH_REPORT,
    REALIZED_FIELD,
    REPORTED_FIELD,
    SECTION_FIELD,
    SERVICE_PROPOSAL_AMOUNT,
    SERVICE_PROPOSAL_AMOUNT_WITH_REPORT,
    SIMULATED_ARBITRATED_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT_WITH_REPORT,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    SUPPLEMENTARY_BUDGET_AMOUNT_WITH_REPORT,
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
} from 'routes/BudgetModule/config/constants';

type BalanceData = {
    // Budget fields
    [BUDGET_ID_FIELD]: number;
    [BUDGET_NAME_FIELD]: string;
    // Chapter fields
    [CHAPTER_FIELD]: string;
    [CHAPTER_LABEL_FIELD]: string;
    // Nature fields
    [NATURE_FIELD]: string;
    // Budget line fields
    [BUDGET_YEAR_FIELD]: number;
    [SECTION_FIELD]: string;
    [EXPENSE_INCOME_FIELD]: string;
    // Amount fields
    [REALIZED_FIELD]: number;
    [REPORTED_FIELD]: number;
    [VOTED_FIELD]: number;
    [VOTED_TOTAL_FIELD]: number;
    [BUDGET_BALANCE_TOTAL_FIELD]: number;
    [BUDGET_BALANCE_TOTAL_FIELD_WITH_REPORT]: number;
    [SERVICE_PROPOSAL_AMOUNT]: number;
    [SERVICE_PROPOSAL_AMOUNT_WITH_REPORT]: number;
    [PRIMITIVE_BUDGET_AMOUNT]: number;
    [PRIMITIVE_BUDGET_AMOUNT_WITH_REPORT]: number;
    [SIMULATED_ARBITRATED_AMOUNT]: number;
    [SIMULATED_ARBITRATED_AMOUNT_WITH_REPORT]: number;
    [SUPPLEMENTARY_BUDGET_AMOUNT]: number;
    [SUPPLEMENTARY_BUDGET_AMOUNT_WITH_REPORT]: number;
};

export const useGetBalanceData = (
    year: number,
    expenseIncome: BudgetExpenseOrIncome,
    section: string,
    budgetName: string | null | undefined,
): UseQueryResult<BalanceData[]> => {
    const filters: QueryFilterT[] = [
        {
            field: 'budget_line.year',
            value: year.toString(),
            operation: FilterOperations.equal,
        },
        {
            field: 'budget_line.expense_income',
            operation: FilterOperations.in_,
            value: expenseIncome.toUpperCase(),
        },
        {
            field: 'budget_line.investment_operating',
            operation: FilterOperations.in_,
            value: section,
        },
    ];

    if (budgetName && budgetName !== ALL_BUDGETS_VALUE) {
        filters.push({
            field: 'budget.name',
            value: budgetName,
            operation: FilterOperations.equal,
        });
    }

    return useQuery({
        queryKey: [
            'budget_balance_data',
            year.toString(),
            expenseIncome,
            section,
            budgetName,
        ],

        queryFn: async () => {
            const response = await RQAPICallWithData<BalanceData[]>({
                endpoint: `budget_proposals/get_balance_data?${queryToSearch({
                    filters,
                    metrics: [],
                    dimensions: [],
                })}`,
                method: 'GET',
                preProcessingUrlCallback: (url) =>
                    transformUrlParams(url, ['filter'], snakify),
            });
            return response.content.data;
        },
    });
};
